import AddIcon from '@mui/icons-material/Add';
import { Box, Button, ClickAwayListener, Divider, Grid, IconButton, ListItem, ListItemText, Paper, Popper, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router';
import { getGridStringOperators } from '@mui/x-data-grid';

import { DataAPIContext } from '../api';
import { GET_ONE, UPDATE } from '../api/dataProvider';
import useBatchDelete from '../bg_tasks/useBatchDelete';
import AccessDeniedView from '../components/AccessDeniedView';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { onTourColumn, onTourLabel } from '../components/onTourColumn';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import TabPanel, { a11yProps } from '../components/TabPanel';
import DeleteIconButton from '../components/DeleteIconButton';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import PreTripCategoryEdit from './PreTripCategoryEdit';
import PreTripItemEdit from './PreTripItemEdit';
import PreTripItemListToolbar from './PreTripItemListToolbar';
import { ConfirmationDialogText, guestsSeeText } from './utils';
import { renderingTypeLabel } from './utils';
import { getPreviousLinkFileOrTextState } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        border: 'solid 1px #D8D8D8 !important',
        borderRadius: '12px',
        borderLeft: '0px !important',
        borderBottom: '0px !important',
        borderRight: '0px !important',
    },
    recordList: {
        marginTop: '-0.25rem',
        height: `calc(${theme.cp.contentHeight} - 9.5rem - 64px) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    sortButton: {
        minWidth: theme.spacing(4),
        minHeight: theme.spacing(4),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        padding: theme.spacing(1),
        marginRight: theme.spacing(1),
    },

    tabsWrapper: {
        borderLeft: 'solid 1px #D8D8D8',
        borderRadius: '12px 12px 0 0',
        borderRight: 'solid 1px #D8D8D8',
        borderBottom: 'solid 1px #D8D8D8',
    },
    toolbarWrapper: {
        borderLeft: 'solid 1px #D8D8D8',
        padding: '0.5rem 1rem 1rem 1rem',
        borderRight: 'solid 1px #D8D8D8',

        [theme.breakpoints.down('sm')]: {
            marginBottom: '-0.25rem !important',
            padding: '0.25rem 1rem 0.5rem 1rem',
        },
    },
}));

const emptyCategory = {
    name: '',
    rendering_type: 0,
};

const filterIds = (ids, objects) => {
    if (ids.length === 0 || objects.length === 0) {
        return ids;
    }
    const objectIds = new Set(objects.map(obj => obj.item.id));

    // Filter out ids that are found in the objectIds Set
    return ids.filter(id => !objectIds.has(id));
}

const PreTripItemList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const urlParams = useParams();
    const history = useHistory();
    const classes = useStyles();
    const [query, setQuery] = React.useState(null);
    const [syncActive, setSyncActive] = React.useState(false);
    const [activeItem, setActiveItem] = React.useState(null);
    const [, setDetailsDirty] = React.useState(false);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    // const [newRowOrder, setNewRowOrder] = React.useState({});
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [currentTab, setCurrentTab] = React.useState(window.location.href.includes('category') ? 1 : 0);
    const [resource, setResource] = React.useState(currentTab === 0 ? 'pre_trip_item' : 'pre_trip_item_type');
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const btSize = isSmallScreen ? 'medium' : 'small';
    const [recordListFilters, setRecordListFilters] = React.useState(null);
    const [deleteItemFromCategoryEditPage, setDeleteItemFromCategoryEditPage] = React.useState(null);
    const [tripSelectAnchorEl, setTripSelectAnchorEl] = React.useState(null);
    const [duplicateSelectAnchorEl, setDuplicateSelectAnchorEl] = React.useState(null);
    const [selectedTrip, setSelectedTrip] = React.useState(null);
    const [selectedTripDuplicateItems, setSelectedTripDuplicateItems] = React.useState([])

    const onQueryChange = (query) => {
        setQuery(query);
    };

    // Stable: RecordList drops a "select all matching" whenever apiFilters changes identity.
    const itemApiFilters = React.useMemo(() => [{ field: 'item_type', value: recordListFilters }], [recordListFilters]);

    const onSelectionModelChange = async (ids, selection) => {
        setSelectedIds(selection);
    };

    // CP-7771: bulk delete runs as a background task; the grid refreshes once it has finished.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords: deleteItems } = useBatchDelete({ resource: 'pre_trip_item', names: ['trip item', 'trip items'], onDeleted: refreshList });
    const { deleteRecords: deleteCategories } = useBatchDelete({ resource: 'pre_trip_item_type', names: ['trip item category', 'trip item categories'], onDeleted: refreshList });

    const doDeleteItemList = (idlist) => {
        setSyncActive(true);
        setActiveItem(null);
        const deleteRecords = deleteItemFromCategoryEditPage || resource === 'pre_trip_item' ? deleteItems : deleteCategories;
        deleteRecords(deleteItemFromCategoryEditPage ? [deleteItemFromCategoryEditPage] : idlist)
            .finally(() => {
                setSelectedIds([]);
                setSyncActive(false);
                if (currentTab === 0) {
                    history.push(`/pretrip/items`);
                } else {
                    history.push(`/pretrip/items/category`);
                }
                setDeleteItemFromCategoryEditPage(null)
            });
    };

    const onDeleteItemList = (idlist, delete_from_edit_category_page) => {
        if (currentTab === 0 || delete_from_edit_category_page) {
            setConfirmationDialogAction(idlist.length > 1 ? 'delete_pretrip_items' : 'delete_pretrip_item');
        } else {
            setConfirmationDialogAction(idlist.length > 1 ? 'delete_pretrip_categories' : 'delete_pretrip_category');
        }
        setSelectedIds(idlist);
    };


    const onItemDelete = (f) => {
        return doDeleteItemList([f.id]);
    };


    const onRowClick = (p) => {
        if (currentTab === 0) {
            history.push(`/pretrip/items/${p.row.id}`);
        } else {
            history.push(`/pretrip/items/category/${p.row.id}`);
        }
    };

    const onDetailsClose = (ev) => {
        setActiveItem(null);
        setDetailsDirty(false);
        if (currentTab === 0) {
            history.push('/pretrip/items');
        } else {
            history.push(`/pretrip/items/category`);
        }
    };

    const onItemChange = (fn) => {
        setLastUpdate(new Date().toString());
    };

    React.useEffect(() => {
        if (!urlParams || !urlParams.id) {
            setActiveItem(null);
            setSelectedIds([]);
            return;
        }
        dataAPI(GET_ONE, resource, { id: urlParams.id }).then((res) => {
            setActiveItem(res.data);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParams.id]);

    const cols = isSmallScreen ? (activeItem ? 0 : 12) : activeItem ? 8 : 12;

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'delete_pretrip_item':
            case 'delete_pretrip_items':
                doDeleteItemList(selectedIds);
                break;
            case 'delete_pretrip_category':
            case 'delete_pretrip_categories':
                doDeleteItemList(selectedIds);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const handleTabChange = (ev, newTab) => {
        setActiveItem(null);
        setRecordListFilters(null);
        setCurrentTab(newTab);
        setSelectedIds([]);
        setResource(newTab === 0 ? 'pre_trip_item' : 'pre_trip_item_type');
        history.push(newTab === 0 ? '/pretrip/items' : '/pretrip/items/category');
    };
    const tabPanelProps = (index, h) => {
        return {
            className: classes.tabPanel,
            value: currentTab,
            index: index,
            // dir: theme.direction,
            style: {
                // height: isSmallScreen ? '72.5vh' : null,
            },
        };
    };

    const listItemRenderer = (row, idx, rows, gridApi) => {
        return (
            <React.Fragment>
                <ListItem alignItems="flex-start" className={classes.listItem} onClick={(ev) => onRowClick({ row, idx }, ev)}>
                    <ListItemText primary={row.name} secondary={[guestsSeeText(row), row.item_type?.name, onTourLabel(row)].filter(Boolean).join(' · ')} />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    const categoryListItemRenderer = (row, idx, rows, gridApi) => {
        return (
            <React.Fragment>
                <ListItem alignItems="flex-start" className={classes.listItem} onClick={(ev) => onRowClick({ row, idx }, ev)}>
                    <ListItemText primary={row.name} secondary={renderingTypeLabel(row.rendering_type)} />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    const onCreateCategory = () => {
        history.push('/pretrip/items/category');
        setActiveItem({ ...emptyCategory });
    };

    const onCreateNewItemBtClick = (ev) => {
        history.push('/pretrip/items/create');
    };

    const handleAddItemsToTrip = (ev, skip_duplicate_check, remove_duplicates) => {

        let trip = selectedTrip;

        const getPreviousTripData = async () => {
            setSyncActive(true);
            let res = await dataAPI(GET_ONE, `tour`, { id: trip.id });
            setSelectedTrip(res.data);
            setSyncActive(false);
            updateSelectedTripData(res.data);
        };
        getPreviousTripData();

        const updateSelectedTripData = async (previousData) => {
            let duplicate_items = []
            let existing_pre_trip_items = previousData.pre_trip_items;
            for (let index = 0; index < previousData.pre_trip_items.length; index++) {
                let duplicate_item = selectedIds.filter(id => id === previousData.pre_trip_items[index].item.id).length > 0 ? previousData.pre_trip_items[index].item : null;
                if (duplicate_item) {
                    duplicate_items = [...duplicate_items, duplicate_item]
                }
            }
            if (duplicate_items.length > 0 && !skip_duplicate_check) {
                setSelectedTripDuplicateItems(duplicate_items.map((item, index) => ({ ...item, idx: index })));
                setDuplicateSelectAnchorEl(true);
                setTripSelectAnchorEl(null);
                return;
            }

            let params = { data: { ...previousData, pre_trip_items: [...previousData.pre_trip_items] }, id: trip.id };
            let new_selected_ids = [...selectedIds]


            if (remove_duplicates) {
                new_selected_ids = filterIds(selectedIds, existing_pre_trip_items)
            }

            let new_items = new_selected_ids.map((id, index) => ({ id: null, sequence: existing_pre_trip_items.length + index, item: { id: id } }));
            params.data.pre_trip_items = [...existing_pre_trip_items, ...new_items];
            if (remove_duplicates) {

            }
            setSyncActive(true);
            let update_res = await dataAPI(UPDATE, `tour`, params);
            if (update_res.statusCode === 200) {
                enqueueSnackbar('Trip items added', { variant: 'success' });
            }
            setSelectedIds([]);
            setSyncActive(false);
            setLastUpdate(new Date().toString());
            setSelectedTrip(null);
            setTripSelectAnchorEl(null);
            setDuplicateSelectAnchorEl(null);

        };
    };

    const handleSelectTrip = (ev, trip) => {
        setSelectedTrip(trip);
    };
    const handleAddItemsWithoutDuplicatesToTrip = () => {
        handleAddItemsToTrip('ev', true, true)
    }


    if (!hasPerm('pre_trip_item', 'list')) {
        return <AccessDeniedView />
    }
    return (
        <>
            <PageMeta title="Trip Items" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            <Popper open={Boolean(tripSelectAnchorEl)} style={{ zIndex: 1 }} anchorEl={tripSelectAnchorEl} onClose={() => setTripSelectAnchorEl(null)}>
                <ClickAwayListener
                    onClickAway={() => {
                        setTripSelectAnchorEl(null);
                    }}
                >
                    <Paper style={{ position: 'fixed', top: '10rem', left: '40%', border: 'solid 1px #8d8d8d', padding: '3rem 2rem', borderRadius: '12px' }}>
                        <Typography fontWeight={500} mb={4}>
                            ADD TO TRIP
                        </Typography>
                        <Typography>To what trip do you want to add these trip items?</Typography>
                        <RemoteAutocomplete
                            InputProps={{ required: true }}
                            resource="tour"
                            label="Select Trip"
                            variant="filled"
                            size="small"
                            onChange={handleSelectTrip}
                            value={selectedTrip}
                            getOptionLabel={(opt) => {
                                if (!opt.is_bespoke) {
                                    return opt.name + ' (Recurring)';
                                }
                                return opt.name + ' (' + opt.start_date + ' - ' + opt.end_date + ')';
                            }}
                        />
                        <FormActionButtons
                            size="medium"
                            sx={{ mt: 2 }}
                            onCancel={() => setTripSelectAnchorEl(null)}
                            onSubmit={handleAddItemsToTrip}
                            submitLabel={`Add item to trip (${selectedIds.length})`}
                        />
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Found Duplicate Popper */}
            <Popper open={Boolean(duplicateSelectAnchorEl)} style={{ zIndex: 1 }} anchorEl={duplicateSelectAnchorEl} onClose={() => setDuplicateSelectAnchorEl(null)}>
                <ClickAwayListener
                    onClickAway={() => {
                        setDuplicateSelectAnchorEl(null);
                    }}
                >
                    <Paper style={{ position: 'fixed', top: '10rem', left: '40%', border: 'solid 1px #8d8d8d', padding: '3rem 2rem', borderRadius: '12px' }}>
                        <Typography fontWeight={500} mb={4}>
                            Duplicate items
                        </Typography>
                        <Typography>These trip items are already in the selected tour.</Typography>
                        <Box style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {selectedTripDuplicateItems.map(item => <Grid item xs={12} key={item.idx} style={{ borderBottom: selectedTripDuplicateItems.length - 1 !== item.idx ? 'solid 1px #D8D8D8' : 'none', padding: '0.5rem 0' }}>
                                {item.name}
                            </Grid>)}
                        </Box>
                        <Grid container spacing={1} style={{ marginTop: '1rem' }}>
                            <Grid item>
                                <Button variant="contained" onClick={handleAddItemsWithoutDuplicatesToTrip}>
                                    SKIP DUPLICATES
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button style={{ border: 'solid 1px #D8D8D8' }} variant="outlined" onClick={(ev) => handleAddItemsToTrip(ev, true)}>
                                    ADD ANYWAY
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </ClickAwayListener>
            </Popper>
            <Grid container direction="row" p={2}>
                <Grid container>
                    <Grid item xs={8} pb={1}>
                        <Typography fontWeight={500} fontSize={20}>
                            {currentTab === 0 ? 'Trip Items' : 'Trip Item Categories'}
                        </Typography>
                    </Grid>
                    <Grid item xs={4} pb={1} container justifyContent="flex-end">
                        <Grid item xs={4} sm={8} container direction="row" justifyContent="flex-end" alignItems="center">
                       
                            {!hasPerm('pre_trip_item', 'write') || isSmallScreen || selectedIds?.length > 0 ? null : (
                                <Box mr={2}>
                                    <Button color="primary" variant="contained" size={btSize} onClick={resource === 'pre_trip_item' ? onCreateNewItemBtClick : onCreateCategory}>
                                        <AddIcon />
                                        &nbsp;{resource === 'pre_trip_item' ? (!isSmallScreen ? 'New trip item' : null) : !isSmallScreen ? 'New category' : null}
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container direction="row" className={classes.root}>
                    {isSmallScreen && activeItem ? (
                        <></>
                    ) : (
                        <>
                            <Grid item xs={12} className={classes.tabsWrapper}>
                                <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" className={classes.tabsMenu}>
                                    <Tab label="Trip Items" {...a11yProps(0)} />
                                    <Tab label="Categories" {...a11yProps(1)} />
                                </Tabs>
                            </Grid>

                            <Grid item xs={12} className={classes.toolbarWrapper}>
                                <PreTripItemListToolbar resource={resource} onQueryChange={onQueryChange} recordListFilters={recordListFilters} setRecordListFilters={setRecordListFilters} isSmallScreen={isSmallScreen} onCreateCategory={onCreateCategory} selectedIds={selectedIds} setSelectedIds={setSelectedIds} setLastUpdate={setLastUpdate} />
                            </Grid>
                            <Grid item xs={cols}>
                                <TabPanel {...tabPanelProps(0, '100%')}>
                                    <RecordList
                                        className={classes.recordList}
                                        apiFilters={itemApiFilters}
                                        resource={resource}
                                        disableSelectionOnClick={true}
                                        // control the selected rows state
                                        selectionModel={selectedIds}
                                        onSelectionModelChange={onSelectionModelChange}
                                        deps={lastUpdate}
                                        query={query}
                                        perPage={100}
                                        checkboxSelection={hasPerm('tour', 'write') || hasPerm('pre_trip_item', 'delete')}
                                        listItemRenderer={listItemRenderer}
                                        disableColumnMenu={false}
                                        disableListBorder={false}
                                        limitHeightForMobile={'calc(100vh - 16rem)'}
                                        columns={[
                                            ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                                            { field: 'name', headerName: 'Name', sortable: false, filterable: false, flex: 1 },
                                            // CP-6187: what guests see in the app (blank: the Name). Search finds it too.
                                            { field: 'friendly_name', headerName: 'Friendly Name', sortable: false, filterable: false, flex: 1, valueGetter: (p) => p.row.friendly_name || '' },
                                            onTourColumn(),
                                            { field: 'note', headerName: 'Note', sortable: false, filterable: false, flex: 1 },
                                            { field: 'type', headerName: 'Category', sortable: false, filterable: false, flex: 0.5, valueGetter: (p) => (p.row.item_type ? p.row.item_type.name : '[not set]') },
                                            { field: "", headerName: 'Type', sortable: false, filterable: false, width: 100, renderCell: (p) => <Typography sx={{ textTransform: 'capitalize', backgroundColor: "#F2F2F2", padding: '0.2rem 0.5rem', fontSize: '12px' }}> {getPreviousLinkFileOrTextState(p.row)}</Typography> },
                                            // { field: 'item_type', headerName: 'Type', sortable: false, flex: 0.5, valueGetter: (p) => getRenderingTypeLabel(p.row.item_type.rendering_type) },
                                            { field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', '),
                                                filterOperators: getGridStringOperators().filter(
                                                    (op) => op.value === 'contains'
                                                ),                                    
                                            },
                                            {
                                                field: 'action',
                                                headerName: 'Action',
                                                sortable: false,
                                                filterable: false,
                                                width: 100,
                                                renderCell: (p) => (
                                                    <Grid container>
                                                        <Grid item>
                                                            <Tooltip title="Edit">
                                                                <IconButton color="primary" onClick={() => {
                                                                    if (currentTab === 0) {
                                                                        history.push(`/pretrip/items/${p.row.id}`);
                                                                    } else {
                                                                        history.push(`/pretrip/items/category/${p.row.id}`);
                                                                    }
                                                            }}>
                                                                    {/* <IconButton color="primary" onClick={(e) => handleOnItemEdit(e, p.row.id)}> */}
                                                                    <EditIcon color="primary" fontSize="small" />
                                                            </IconButton>
                                                            </Tooltip>
                                                        </Grid>
                                                        <Grid item>
                                                            <DeleteIconButton perm="pre_trip_item.delete" size="medium" onClick={() => onDeleteItemList([p.row.id])} />
                                                        </Grid>
                                                    </Grid>
                                                ),
                                            },
                                        ]}
                                    />
                                </TabPanel>
                                <TabPanel {...tabPanelProps(1, '100%')}>
                                    <RecordList
                                        className={classes.recordList}
                                        resource={resource}
                                        checkboxSelection={hasPerm('pre_trip_item', 'write') || hasPerm('pre_trip_item', 'delete')}
                                        disableSelectionOnClick={true}
                                        selectionModel={selectedIds}
                                        onSelectionModelChange={onSelectionModelChange}
                                        limitHeightForMobile={'calc(100vh - 16rem)'}
                                        deps={lastUpdate}
                                        query={query}
                                        perPage={100}
                                        listItemRenderer={categoryListItemRenderer}
                                        disableColumnMenu={true}
                                        disableListBorder={false}
                                        columns={[
                                            ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                                            { field: 'name', headerName: 'Display name', sortable: false, flex: 1 },
                                            { field: 'system_name', headerName: 'System name', sortable: false, filterable: false, flex: 1 },
                                            { field: 'count', headerName: 'Items assigned', sortable: false, flex: 0.25, valueGetter: (p) => (p.row.count) },
                                            { field: 'rendering_type', headerName: 'Type', sortable: false, flex: 0.5, valueGetter: (p) => renderingTypeLabel(p.row.rendering_type) },
                                            {
                                                field: 'action',
                                                headerName: 'Action',
                                                sortable: false,
                                                width: 100,
                                                renderCell: (p) => (
                                                    <Grid container>
                                                        <Grid item>
                                                            <Tooltip title="Edit">
                                                                <IconButton color="primary" onClick={() => {
                                                                    if (currentTab === 0) {
                                                                        history.push(`/pretrip/items/${p.row.id}`);
                                                                    } else {
                                                                        history.push(`/pretrip/items/category/${p.row.id}`);
                                                                    }
                                                                }}>      <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            </Tooltip>
                                                        </Grid>
                                                        <Grid item>
                                                            <DeleteIconButton perm="pre_trip_item.delete" size="medium" onClick={() => onDeleteItemList([p.row.id])} />
                                                        </Grid>
                                                    </Grid>
                                                ),
                                            },
                                        ]}
                                    />
                                </TabPanel>
                            </Grid>
                        </>
                    )}
                    {activeItem ? (
                        <Grid item xs={12 - cols}>
                            {currentTab === 0 ? (
                                <PreTripItemEdit key={activeItem.id} resource={resource} data={activeItem} itemId={urlParams.id} onClose={onDetailsClose} onChange={onItemChange} onDelete={onItemDelete} syncActive={syncActive} setSyncActive={setSyncActive} />
                            ) : (
                                    <PreTripCategoryEdit resource={resource} handleTabChange={handleTabChange} catId={urlParams.id} onDeleteItemList={onDeleteItemList} setDeleteItemFromCategoryEditPage={setDeleteItemFromCategoryEditPage} data={activeItem} onClose={onDetailsClose} onChange={onItemChange} onDelete={onItemDelete} syncActive={syncActive} setSyncActive={setSyncActive} setDetailsDirty={setDetailsDirty} />
                            )}
                        </Grid>
                    ) : null}
                </Grid>
            </Grid>
            {/* CP-8423 (53): [DELETE (N)] [CANCEL] [ADD ITEM TO TRIP (N)] bottom-right. CANCEL clears the
                selection; ADD ITEM TO TRIP only on the Trip Items tab, for users who can edit trips.
                CP-5545 (52): the Categories tab reads [DELETE CATEGORY (N)] [CANCEL]; DELETE is disabled, not
                hidden, without pre_trip_item.delete. */}
            {!isSmallScreen && <Grid container xs={12} justifyContent="flex-end" alignItems="center" p={1} style={{ backgroundColor: '#F5F5F5', height: '64px' }} >
                {selectedIds?.length > 0 && (
                    <FormActionButtons
                        size={btSize}
                        onDelete={() => onDeleteItemList(selectedIds)}
                        deletePerm="pre_trip_item.delete"
                        deleteLabel={currentTab === 1 ? `Delete category (${selectedIds.length})` : `Delete (${selectedIds.length})`}
                        onCancel={() => setSelectedIds([])}
                        onSubmit={resource === 'pre_trip_item' && hasPerm('tour', 'write') ? () => setTripSelectAnchorEl(true) : undefined}
                        submitLabel={`Add item to trip (${selectedIds.length})`}
                    />
                )}
            </Grid >}
        </>
    );
};

export default PreTripItemList;
